import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';

const RESEND_COOLDOWN_SEC = 45;
const OTP_TTL_SEC = 600;

const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { verifyOtp, resendOtp, isLoading } = useAuthStore();

  const qsEmail = searchParams.get('email') || '';
  const initialEmail = useMemo(
    () => location.state?.email || sessionStorage.getItem('pendingEmail') || qsEmail,
    [location.state, qsEmail]
  );
  const [email, setEmail] = useState(initialEmail);

  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const inputsRef = useRef([]);

  const [cooldown, setCooldown] = useState(0);
  const [timeLeft, setTimeLeft] = useState(OTP_TTL_SEC);
  const expired = timeLeft <= 0;

  const shownAutoSentRef = useRef(false);

  useEffect(() => {
    if (location.state?.autoSent && !shownAutoSentRef.current) {
      shownAutoSentRef.current = true;
      toast.info('We sent you a new OTP. Please check your email.', { toastId: 'otp-auto-sent' });
      setTimeout(() => navigate('.', { replace: true, state: {} }), 0);
    }
    const saved = location.state?.email || sessionStorage.getItem('pendingEmail') || qsEmail;
    if (saved && !email) setEmail(saved);
  }, [location.state, navigate]);

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    if (expired) return;
    const t = setInterval(() => setTimeLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [expired]);

  const mm = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const ss = String(timeLeft % 60).padStart(2, '0');

  const focusIndex = (idx) => {
    const el = inputsRef.current[idx];
    if (el) el.focus();
  };

  const handleChange = (idx, value) => {
    const v = value.replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    if (v && idx < 5) focusIndex(idx + 1);
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      if (digits[idx]) {
        const next = [...digits];
        next[idx] = '';
        setDigits(next);
      } else if (idx > 0) {
        focusIndex(idx - 1);
      }
    }
    if (e.key === 'ArrowLeft' && idx > 0) focusIndex(idx - 1);
    if (e.key === 'ArrowRight' && idx < 5) focusIndex(idx + 1);
  };

  const handlePaste = (e) => {
    if (expired) return;
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const next = [...digits];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || '';
    setDigits(next);
    const last = Math.min(pasted.length, 6) - 1;
    focusIndex(last >= 0 ? last : 0);
  };

  const code = digits.join('');
  const canSubmit = !!email && code.length === 6 && !isLoading && !expired;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const type = location.state?.type;

    const res = await verifyOtp({ email, otp_code: code });
    if (res.success) {
      if (type === 'reset') {
        toast.success('OTP verified!');
        navigate('/reset-password', { state: { email, otp: code }, replace: true });
      } else {
        toast.success('Your account has been verified!');
        sessionStorage.removeItem('pendingEmail');
        navigate('/dashboard');
      }
    } else {
      toast.error(res.error || 'OTP verification failed');
    }
  };

  const onResend = async () => {
    if (!email) {
      toast.error('Missing email to resend OTP.');
      return;
    }
    const res = await resendOtp({ email });
    if (res.success) {
      toast.success('OTP resent. Please check your inbox.');
      setCooldown(RESEND_COOLDOWN_SEC);
      setTimeLeft(OTP_TTL_SEC);
      setDigits(['', '', '', '', '', '']);
      focusIndex(0);
    } else {
      toast.error(res.error || 'Failed to resend OTP');
    }
  };

  const handleGoBack = () => {
    sessionStorage.removeItem('pendingEmail');
    navigate('/login', { replace: true });
  };

  return (
    <div className="w-full">
      <div className="text-left mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Verify Email</h2>
        <p className="text-sm text-gray-400 font-medium tracking-tight leading-relaxed">
          We've sent a code to <span className="text-white font-bold">{email}</span>.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-8">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold text-gray-300 ml-1 uppercase tracking-wider opacity-70">Verification Code</label>
            <span className={`text-[10px] font-bold px-3 py-1 rounded-full ${expired ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-primary-500/10 text-primary-500 border border-primary-500/20'}`}>
              {expired ? 'Expired' : `${mm}:${ss}`}
            </span>
          </div>

          <div
            className="flex items-center justify-between gap-2.5"
            onPaste={handlePaste}
          >
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                value={d}
                inputMode="numeric"
                maxLength={1}
                disabled={expired}
                onChange={(e) => !expired && handleChange(i, e.target.value)}
                onKeyDown={(e) => !expired && handleKeyDown(i, e)}
                className={`flex-1 h-12 rounded-[0.75rem] border text-center text-xl font-bold transition-all shadow-lg
                  ${expired
                    ? 'bg-[#1A1F26] border-white/5 text-gray-600'
                    : 'bg-[#1A1F26] border-white/5 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 text-white'
                  }`}
                aria-label={`OTP digit ${i + 1}`}
              />
            ))}
          </div>

          {expired && (
            <p className="mt-4 text-[10px] font-bold text-red-400 text-center uppercase tracking-wide">
              Code has expired. Please request a new one.
            </p>
          )}
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full flex justify-center py-3.5 px-6 rounded-[1rem] shadow-[0_20px_40px_-12px_rgba(79,70,229,0.4)] text-base font-bold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              'Verify'
            )}
          </button>
        </div>
      </form>

      <div className="mt-10 text-center space-y-4">
        <button
          type="button"
          onClick={onResend}
          disabled={cooldown > 0}
          className="text-xs font-bold text-primary-500 hover:text-primary-400 transition-colors disabled:opacity-50"
        >
          {cooldown > 0 ? `Resend Code in ${cooldown}s` : 'Resend Verification Code'}
        </button>

        <div className="text-xs font-semibold text-gray-500">
          Wrong email address?{' '}
          <button
            type="button"
            onClick={handleGoBack}
            className="text-primary-500 font-bold hover:text-primary-400 transition-colors ml-1"
          >
            Change email
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;